/**
 * "Запланированные задачи" widget + page (Wave 5, feature #8 / Scheduler) — redesign §6.
 *
 * Reads useJobs({status:"scheduled"}) — the user's own SCHEDULED jobs — and renders
 * each as a row in a panel table with the kind label, the local-formatted run_at (mono),
 * a status pill (tones) and an "Отменить" button (useCancelJob). The list polls
 * (refetchInterval in useJobs) so a job that fires/finishes drops off the scheduled list
 * automatically.
 *
 * Stat tiles use only data we actually have: number of scheduled jobs, plus done/failed
 * job counts (separate useJobs calls). We deliberately do NOT invent metrics the README
 * mockup shows (uptime / runs-today) — those have no backing field in GET /api/jobs.
 */

import { type ReactNode } from "react";
import { X, AlertTriangle } from "lucide-react";

import { type JobListItem, type JobInfo } from "@/api/client";
import { useJobs, useActiveJobs, useCancelJob } from "@/api/hooks";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Pill } from "@/components/ui/Pill";
import { TouchLink } from "@/components/jobs/TouchLink";
import { type Tone } from "@/lib/tones";

/** Map a job kind to a human label. Falls back to the raw kind for unknown ones. */
const KIND_LABELS: Record<string, string> = {
  touch_fill: "Наполнение касания",
  bulk_delete: "Массовое удаление",
  readiness_fill: "Готовность из файла",
  bulk_edit_file: "Обновление из файла",
  uik_branch_fill: "УИК → отделение из файла",
  lottery_turnout: "ЕДГ-лотерея: явка",
  export: "Экспорт",
};

function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}

/** Prefer the backend-provided description; fall back to the kind label when null/empty. */
function jobLabel(job: Pick<JobListItem, "kind" | "description">): string {
  const d = job.description?.trim();
  return d && d.length > 0 ? d : kindLabel(job.kind);
}

/**
 * Render the job's label. For touch_fill jobs (touch_id present) the touch number is a
 * clickable <TouchLink/> showing the touch name, with an optional « · результат «…»»
 * suffix. Other kinds fall back to the plain text jobLabel().
 */
function JobLabel({ job }: { job: Pick<JobListItem, "kind" | "description" | "touch_id" | "result"> }) {
  if (job.touch_id != null) {
    const result = job.result?.trim();
    return (
      <>
        {"Наполнение касания "}
        <TouchLink touchId={job.touch_id} />
        {result ? ` · результат «${result}»` : ""}
      </>
    );
  }
  return <>{jobLabel(job)}</>;
}

/** Format an ISO-8601 string as дд.мм.гггг чч:мм (local), or "—" when empty/invalid. */
function formatDateTime(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${d.getFullYear()} ${hh}:${mi}`;
}

/** Accent fill drawn with the literal accent hex (design: real hex on the bar, not var(--accent)). */
const ACCENT_HEX = "var(--accent-hex, #2563eb)";

/** Map a job status to a pill label + semantic tone (README §6: Успех/Идёт/Запланировано/Ошибка). */
const STATUS_META: Record<JobInfo["status"], { label: string; tone: Tone }> = {
  done: { label: "Успех", tone: "green" },
  running: { label: "Идёт", tone: "blue" },
  pending: { label: "В очереди", tone: "blue" },
  scheduled: { label: "Запланировано", tone: "gray" },
  failed: { label: "Ошибка", tone: "red" },
  cancelled: { label: "Отменена", tone: "amber" },
};

function StatusPill({ status }: { status: JobInfo["status"] }) {
  const meta = STATUS_META[status] ?? { label: status, tone: "gray" as Tone };
  return (
    <Pill tone={meta.tone} withDot>
      {meta.label}
    </Pill>
  );
}

/** Format an ISO-8601 UTC string as a local date+time (ru-RU), or "—". */
function formatRunAt(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("ru-RU", { dateStyle: "medium", timeStyle: "short" });
}

function ScheduledJobRow({ job }: { job: JobListItem }) {
  const cancel = useCancelJob();

  return (
    <div
      className="grid grid-cols-[minmax(0,1.4fr)_180px_140px_88px] items-center gap-3 border-t border-[var(--border-soft)] px-4 py-2.5 text-sm first:border-t-0 hover:bg-muted/40"
      data-testid="scheduled-job"
    >
      {/* Задача / тип */}
      <div className="min-w-0">
        <span className="block truncate font-medium text-foreground"><JobLabel job={job} /></span>
        {/* «≈» — target резолвится в момент запуска и может отличаться (дубликаты/удалённые). */}
        <span className="block text-xs text-muted-foreground">
          #{job.id}
          {job.planned_total != null
            ? ` · ≈ ${job.planned_total.toLocaleString("ru-RU")} чел.`
            : ""}
        </span>
        {cancel.isError && (
          <span className="mt-0.5 block text-xs text-destructive">
            Не удалось отменить
            {cancel.error instanceof Error ? `: ${cancel.error.message}` : "."}
          </span>
        )}
      </div>

      {/* Запуск (run_at, mono) */}
      <span className="font-mono text-xs tabular-nums text-foreground">
        {formatRunAt(job.run_at)}
      </span>

      {/* Статус (пилюля с dot) */}
      <span>
        <StatusPill status={job.status} />
      </span>

      {/* Отмена */}
      <span className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => cancel.mutate(job.id)}
          disabled={cancel.isPending}
        >
          {cancel.isPending ? <Spinner /> : <X className="h-4 w-4" />}
          Отменить
        </Button>
      </span>
    </div>
  );
}

/**
 * One running job: kind label + a progress bar mirroring <ActiveJobsIndicator/>.
 * total<=0 means the selection is still resolving (e.g. sample_n) → indeterminate
 * "Подготовка…" bar instead of a bogus 0/0 0%. failed_count shows in red.
 */
function RunningJobRow({ job }: { job: JobListItem }) {
  const total = job.total || 0;
  const progress = job.progress || 0;
  const preparing = total <= 0;
  const pct = preparing ? 0 : Math.min(100, Math.round((progress / total) * 100));

  return (
    <div className="px-4 py-3" data-testid="running-job">
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate text-sm font-medium text-foreground"><JobLabel job={job} /></span>
        <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
          {preparing
            ? "Подготовка…"
            : `${progress.toLocaleString("ru-RU")} / ${total.toLocaleString("ru-RU")} (${pct}%)`}
        </span>
      </div>

      <div className="mt-2 h-[6px] w-full overflow-hidden rounded-full bg-[var(--border-soft)]">
        {preparing ? (
          <div
            className="h-full w-1/3 animate-pulse rounded-full"
            style={{ backgroundColor: ACCENT_HEX, opacity: 0.6 }}
          />
        ) : (
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, backgroundColor: ACCENT_HEX }}
          />
        )}
      </div>

      <div className="mt-1 flex items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">#{job.id}</span>
        {job.failed_count > 0 && (
          <span className="flex items-center gap-1 text-xs text-[var(--danger,#A93A3A)]">
            <AlertTriangle className="h-3 w-3" />
            Ошибок: {job.failed_count.toLocaleString("ru-RU")}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * "В процессе" section — the user's RUNNING jobs with live progress bars. Polls every
 * ~4s (useActiveJobs) so jobs started elsewhere appear here too. Hidden when there are
 * no running jobs (we show a quiet "нет активных задач" line so the section isn't a
 * mystery gap above the scheduled list).
 */
function RunningJobs() {
  const query = useActiveJobs(4000);
  const jobs = query.data ?? [];

  return (
    <section className="space-y-2" data-testid="running-section">
      <h2 className="text-sm font-semibold tracking-tight text-foreground">В процессе</h2>

      {query.isError ? (
        <div className="rounded-xl border bg-card p-4 text-sm text-destructive">
          Не удалось загрузить активные задачи
          {query.error instanceof Error ? `: ${query.error.message}` : "."}
        </div>
      ) : query.isPending ? (
        <div className="flex items-center gap-2 rounded-xl border bg-card p-4 text-sm text-muted-foreground">
          <Spinner /> Загрузка активных задач…
        </div>
      ) : jobs.length === 0 ? (
        <div className="rounded-xl border bg-card p-6 text-center text-sm text-muted-foreground">
          Нет активных задач.
        </div>
      ) : (
        <div
          className="overflow-hidden rounded-xl border bg-card divide-y divide-[var(--border-soft)]"
          data-testid="running-list"
        >
          {jobs.map((job) => (
            <RunningJobRow key={job.id} job={job} />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Explains where scheduled jobs come from: filling a touch from the Base. Makes the
 * schedule flow discoverable now that there's no separate touches page.
 */
function HowToCreateHint() {
  return (
    <div
      className="rounded-xl border bg-card p-4 text-sm"
      data-testid="scheduled-hint"
    >
      <p className="text-muted-foreground">
        Отложенные задачи создаются при наполнении касания с вкладки «Активисты»: выберите
        активистов, укажите касание в корзине и нажмите «Запланировать», задав время запуска.
      </p>
    </div>
  );
}

/** A single stat tile (README §6 4× row). `tone` colors the value; null = neutral. */
function StatTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: ReactNode;
  tone?: "accent" | "green" | "red";
}) {
  const valueColor =
    tone === "accent"
      ? "var(--accent-hex, #2563EB)"
      : tone === "green"
        ? "var(--success, #11774E)"
        : tone === "red"
          ? "var(--danger, #A93A3A)"
          : undefined;
  return (
    <div className="rounded-xl border bg-card px-4 py-3" data-testid="scheduled-stat">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className="mt-1 text-2xl font-semibold tabular-nums"
        style={valueColor ? { color: valueColor } : undefined}
      >
        {value}
      </div>
    </div>
  );
}

/** Stat tiles: only metrics backed by real data. Uptime / runs-today are omitted on purpose. */
function StatTiles() {
  // "Активных задач" counts RUNNING jobs (in progress), matching the header indicator —
  // not scheduled, which would read 0 while a fill is actually running.
  const running = useActiveJobs(4000);
  const done = useJobs({ status: "done" });
  const failed = useJobs({ status: "failed" });

  const cell = (q: { isPending: boolean; isError: boolean; data?: JobListItem[] }) =>
    q.isError ? "—" : q.isPending ? "…" : (q.data?.length ?? 0);

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="scheduled-stats">
      <StatTile label="Активных задач" value={cell(running)} tone="accent" />
      <StatTile label="Выполнено" value={cell(done)} tone="green" />
      <StatTile
        label="Ошибок"
        value={cell(failed)}
        tone={!failed.isError && !failed.isPending && (failed.data?.length ?? 0) > 0 ? "red" : undefined}
      />
    </div>
  );
}

/**
 * One finished/failed job: label + status badge, "{progress} / {total}" added count,
 * failed_count in red, the created_at timestamp, and (for failures) the error text.
 */
function HistoryJobRow({ job }: { job: JobListItem }) {
  const done = job.status === "done";
  const total = job.total || 0;
  const progress = job.progress || 0;

  return (
    <div className="px-4 py-3" data-testid="history-job">
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
          <JobLabel job={job} />
        </span>
        <Pill tone={done ? "green" : "red"} withDot className="shrink-0">
          {done ? "Выполнено" : "Ошибка"}
        </Pill>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-mono text-xs tabular-nums text-muted-foreground">
          {progress.toLocaleString("ru-RU")} / {total.toLocaleString("ru-RU")}
        </span>
        {job.failed_count > 0 && (
          <span className="flex items-center gap-1 font-mono text-xs tabular-nums text-[var(--danger,#A93A3A)]">
            <AlertTriangle className="h-3 w-3" />
            Ошибок: {job.failed_count.toLocaleString("ru-RU")}
          </span>
        )}
        <span className="ml-auto font-mono text-xs tabular-nums text-muted-foreground">
          {formatDateTime(job.created_at)}
        </span>
      </div>

      {!done && job.error && (
        <p className="mt-1 break-words text-xs text-[var(--danger,#A93A3A)]">{job.error}</p>
      )}
    </div>
  );
}

/**
 * "История" section — the user's terminal jobs (done + failed), merged, sorted by
 * created_at descending, capped to the latest ~20. Each list query polls only while a
 * row is still live (none here), so this is effectively static between navigations.
 */
function HistoryJobs() {
  const done = useJobs({ status: "done" });
  const failed = useJobs({ status: "failed" });

  const isPending = done.isPending || failed.isPending;
  const isError = done.isError || failed.isError;
  const error = (done.error ?? failed.error) as unknown;

  const merged = [...(done.data ?? []), ...(failed.data ?? [])]
    .sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0))
    .slice(0, 20);

  return (
    <section className="space-y-2" data-testid="history-section">
      <h2 className="text-sm font-semibold tracking-tight text-foreground">История</h2>

      {isError ? (
        <div className="rounded-xl border bg-card p-4 text-sm text-destructive">
          Не удалось загрузить историю задач
          {error instanceof Error ? `: ${error.message}` : "."}
        </div>
      ) : isPending ? (
        <div className="flex items-center gap-2 rounded-xl border bg-card p-4 text-sm text-muted-foreground">
          <Spinner /> Загрузка истории…
        </div>
      ) : merged.length === 0 ? (
        <div className="rounded-xl border bg-card p-6 text-center text-sm text-muted-foreground">
          Нет завершённых задач.
        </div>
      ) : (
        <div
          className="overflow-hidden rounded-xl border bg-card divide-y divide-[var(--border-soft)]"
          data-testid="history-list"
        >
          {merged.map((job) => (
            <HistoryJobRow key={job.id} job={job} />
          ))}
        </div>
      )}
    </section>
  );
}

/** The scheduled-tasks list (reusable as a widget). */
export function ScheduledJobs() {
  const query = useJobs({ status: "scheduled" });

  if (query.isPending) {
    return (
      <div className="flex items-center gap-2 rounded-xl border bg-card p-4 text-sm text-muted-foreground">
        <Spinner /> Загрузка запланированных задач…
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="rounded-xl border bg-card p-4 text-sm text-destructive">
        Не удалось загрузить запланированные задачи
        {query.error instanceof Error ? `: ${query.error.message}` : "."}
      </div>
    );
  }

  const jobs = query.data ?? [];

  if (jobs.length === 0) {
    return (
      <div className="rounded-xl border bg-card p-6 text-center text-sm text-muted-foreground">
        Нет запланированных задач.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border bg-card" data-testid="scheduled-table">
      {/* header row */}
      <div className="grid grid-cols-[minmax(0,1.4fr)_180px_140px_88px] gap-3 border-b bg-muted/30 px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <span>Задача</span>
        <span>Запуск</span>
        <span>Статус</span>
        <span className="text-right">Действие</span>
      </div>
      {jobs.map((job) => (
        <ScheduledJobRow key={job.id} job={job} />
      ))}
    </div>
  );
}

/** Full page wrapper used by the /scheduled route. */
export function ScheduledJobsPage() {
  return (
    <div className="flex h-screen flex-col" data-testid="scheduled-page">
      <AppHeader />
      <main className="flex-1 overflow-y-auto px-[22px] py-[18px]">
        <div className="mx-auto max-w-3xl space-y-4">
          <header className="space-y-1">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">Задачи</h1>
            <p className="text-sm text-muted-foreground">
              Активные наполнения и отложенные задачи. Идущие задачи показывают прогресс,
              запланированные можно отменить до запуска.
            </p>
          </header>

          <StatTiles />

          <RunningJobs />

          <section className="space-y-2">
            <h2 className="text-sm font-semibold tracking-tight text-foreground">Запланированные</h2>
            <HowToCreateHint />
            <ScheduledJobs />
          </section>

          <HistoryJobs />
        </div>
      </main>
    </div>
  );
}
